"use client";
import Image from "next/image";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { FaCircleArrowRight } from "react-icons/fa6";
import GlobeStaticFallback from "./GlobeStaticFallback";
import CustomButton from "src/components/atoms/CustomButton";

const AppGlobe = dynamic(() => import("./AppGlobe"), {
  ssr: false,
  loading: () => (
    <div className="w-full h-full flex items-center justify-center">
      <div className="w-3/4 h-3/4 rounded-full bg-gradient-to-br from-cyan-500/10 to-purple-600/10 animate-pulse" />
    </div>
  ),
});

function useShouldRenderGlobe() {
  const [decision, setDecision] = useState(null);

  useEffect(() => {
    const isCoarsePointer = window.matchMedia?.("(pointer: coarse)").matches;
    const isLowMemory = navigator.deviceMemory && navigator.deviceMemory <= 4;
    const saveData = navigator.connection?.saveData;

    if ((isLowMemory || saveData) && isCoarsePointer) {
      setDecision(false);
      return;
    }

    const idleId = window.requestIdleCallback ? window.requestIdleCallback(() => setDecision(true), { timeout: 1600 }) : setTimeout(() => setDecision(true), 300);

    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      else clearTimeout(idleId);
    };
  }, []);

  return decision;
}

export default function AppHeroWithGlobe() {
  const shouldRenderGlobe = useShouldRenderGlobe();

  return (
    <section className="relative min-h-[700px] overflow-hidden bg-blue-800 flex flex-col-reverse lg:flex-row justify-between items-center px-6! sm:px-12! lg:px-10!">
      <div aria-hidden="true" className="absolute inset-0 z-0 block dark:hidden">
        <Image
          src="/images/serviceBanners/homeHeroWhite.webp"
          alt="Professional team collaborating on digital solutions"
          fill
          priority
          sizes="100vw"
          className="object-cover object-center"
          quality={80}
        />
      </div>

      <div aria-hidden="true" className="absolute inset-0 z-0 hidden dark:block">
        <Image
          src="/images/serviceBanners/hero-v3.webp"
          alt="Professional team collaborating on digital solutions"
          fill
          priority
          sizes="100vw"
          className="object-cover object-center"
          quality={80}
        />
      </div>

      <div
        className="absolute inset-0 z-[1] pointer-events-none hidden dark:lg:block"
        style={{
          background: "linear-gradient(to right, var(--c-bg) 0%, var(--c-bg) 30%, rgba(15,23,42,0.7) 40%, rgba(15,23,42,0.3) 47%, transparent 55%)",
        }}
      />

      <div className="relative flex flex-col z-10 w-full lg:w-1/2" style={{ animation: "fadeUp 0.7s 0.1s cubic-bezier(0.16,1,0.3,1) both" }}>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(38px, 6.5vw, 64px)",
            fontWeight: 700,
            color: "var(--c-ink)",
            lineHeight: 1.06,
            letterSpacing: "-0.03em",
            marginBottom: 20,
          }}
        >
          <span
            className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600"
            style={{
              display: "block",
              fontSize: "clamp(18px, 3.2vw, 36px)",
              fontWeight: 400,
              marginBottom: 10,
            }}
          >
            Transform Your
          </span>
          Business With
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600" style={{ marginLeft: "8px" }}>
            AI Automation
          </span>
        </h1>

        <p
          style={{
            fontSize: "clamp(14px, 1.6vw, 20px)",
            color: "var(--text-secondary)",
            lineHeight: 1.75,
            marginBottom: 32,
            maxWidth: 750,
          }}
        >
          We help businesses implement advanced AI solutions that automate repetitive processes, reduce operational costs, improve productivity, and enhance overall business
          efficiency. Our intelligent automation systems and data-driven technologies support faster decision-making, scalable growth, and seamless digital transformation for
          modern enterprises across multiple industries.
        </p>

        <div className="flex flex-col md:flex-row mb-5! mt-10!">
          <CustomButton title="Book Free AI Consultation" href="/contact-us" variant="primary" icon={<FaCircleArrowRight />} />
          <CustomButton title="Our Services" href="#services" variant="outline" />
        </div>
      </div>

      <div className="w-full lg:w-1/2 h-[320px] sm:h-[420px] md:h-[520px] lg:h-[650px] xl:h-[680px] 2xl:h-[700px] relative z-[1] overflow-hidden">
        {shouldRenderGlobe === null && (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-3/4 h-3/4 rounded-full bg-gradient-to-br from-cyan-500/10 to-purple-600/10 animate-pulse" />
          </div>
        )}
        {shouldRenderGlobe === true && <AppGlobe />}
        {shouldRenderGlobe === false && <GlobeStaticFallback />}
      </div>
    </section>
  );
}
